/**
 * AutoHeroLeads — iter177.
 *
 * Auto Hero emails a lead (name + service + message + a portal login link)
 * to the workshop's Outlook inbox; phone/email sit behind the portal login
 * and there's no Auto Hero API. So the SM pastes the email here → we extract
 * the useful bits → they open the portal for phone/email, then SMS the
 * customer straight from Comms. Once the customer texts back, the reply lands
 * in the normal Comms chat + Message inbox.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Plus, ExternalLink, Save, Phone, Mail, Car, Send, Loader2, X,
  Trash2, MessageSquare, ClipboardPaste, Wrench, Check, RefreshCw, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = ['new', 'contacted', 'quoted', 'won', 'lost'];
const STATUS_STYLE = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  quoted: 'bg-violet-100 text-violet-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-slate-200 text-slate-500',
};

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function AutoHeroLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [building, setBuilding] = useState(false);

  // Add-lead modal
  const [addOpen, setAddOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollStatus, setPollStatus] = useState(null);

  // Detail edit fields
  const [edit, setEdit] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // SMS + conversation
  const [smsBody, setSmsBody] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [conversation, setConversation] = useState([]);

  const loadLeads = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const url = new URL(`${BACKEND_URL}/api/comms/leads`);
      if (q) url.searchParams.set('q', q);
      url.searchParams.set('limit', '1000');
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setLeads(data.items || []);
    } catch (e) {
      toast.error(`Failed to load leads — ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(''); }, [loadLeads]);

  const loadPollStatus = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/leads/poll-status`, { credentials: 'include' });
      if (r.ok) setPollStatus(await r.json());
    } catch (_e) { /* noop */ }
  }, []);
  useEffect(() => { loadPollStatus(); }, [loadPollStatus]);
  useEffect(() => {
    const t = setTimeout(() => loadLeads(search), 300);
    return () => clearTimeout(t);
  }, [search, loadLeads]);

  // iter205 — "Poll now" checks BOTH intakes: the lead mailbox (email
  // auto-import, Cloudflare-proof) and the portal scrape (may be blocked).
  const pollNow = async () => {
    setPolling(true);
    try {
      let imported = 0;
      const problems = [];
      const emailConfigured = pollStatus?.email_ingest?.configured;
      if (emailConfigured) {
        try {
          const r = await fetch(`${BACKEND_URL}/api/comms/leads/ingest-emails-now`, {
            method: 'POST', credentials: 'include',
          });
          const d = await r.json().catch(() => ({}));
          if (!r.ok) problems.push(d.detail || 'Email import failed');
          else imported += d.imported || 0;
        } catch (e) { problems.push(`Email import: ${e.message}`); }
      }
      const r2 = await fetch(`${BACKEND_URL}/api/comms/leads/poll-now`, {
        method: 'POST', credentials: 'include',
      });
      const d2 = await r2.json().catch(() => ({}));
      if (!r2.ok) {
        if (!emailConfigured) throw new Error(d2.detail || `HTTP ${r2.status}`);
      } else {
        imported += d2.imported || 0;
      }
      if (imported > 0) {
        toast.success(`${imported} new lead${imported > 1 ? 's' : ''} pulled in`);
        await loadLeads(search);
      } else if (problems.length) {
        toast.warning(problems[0]);
      } else if (!r2.ok && emailConfigured) {
        toast.success("No new lead emails — you're all caught up (portal fetch is blocked by Auto Hero)");
      } else {
        toast.success("No new leads — you're all caught up");
      }
      loadPollStatus();
    } catch (e) {
      toast.error(`Couldn't check for new leads — ${e.message}`);
      loadPollStatus();
    } finally {
      setPolling(false);
    }
  };

  // iter200 — "Build Quote" from a lead: create a draft quote pre-filled with
  // the customer/vehicle (+ the enquiry as a starter line & internal note),
  // linked back via source_lead_id, then jump to the Quote page to price and
  // send it. On send the lead is auto-marked "quoted" → moves to "Quote Sent".
  const buildQuote = async (lead) => {
    if (!lead) return;
    setBuilding(true);
    try {
      const veh = (lead.vehicle || '').trim();
      const parts = veh.split(/\s+/);
      const make = parts[0] || undefined;
      const model = parts.slice(1).join(' ') || undefined;
      const body = {
        customer_name: lead.customer_name || 'New Lead',
        customer_phone: lead.phone || undefined,
        customer_email: lead.email || undefined,
        vehicle_rego: lead.rego || undefined,
        vehicle_make: make,
        vehicle_model: model,
        line_items: [{ description: lead.services || lead.intent || 'Quote', quantity: 1 }],
        internal_notes: lead.message || undefined,
        source_lead_id: lead.lead_id,
      };
      const r = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success('Quote started — price it and send the SMS');
      navigate(`/quotes/${data.quote_id}`);
    } catch (e) {
      toast.error(`Couldn't start the quote — ${e.message}`);
    } finally {
      setBuilding(false);
    }
  };


  const loadConversation = useCallback(async (mobile) => {
    if (!mobile) { setConversation([]); return; }
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/conversation?mobile=${encodeURIComponent(mobile)}`, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      setConversation(data.messages || []);
    } catch { /* silent */ }
  }, []);

  const selectLead = (lead) => {
    setSelected(lead);
    setEdit({
      customer_name: lead.customer_name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      vehicle: lead.vehicle || '',
      status: lead.status || 'new',
      notes: lead.notes || '',
    });
    // iter194 — drop the ready-made job-details message straight into the box
    // so the SM can call/text and get the go-ahead without retyping.
    setSmsBody(lead.suggested_message || '');
    setConversation([]);
    if (lead.phone) loadConversation(lead.phone);
  };

  // iter194 — (re)fetch the customer's phone + email from the Auto Hero portal.
  const fetchContact = async () => {
    if (!selected) return;
    setEnriching(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/leads/${selected.lead_id}/enrich`, {
        method: 'POST', credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Fetch failed');
      setSelected(data);
      setLeads((prev) => prev.map((l) => (l.lead_id === data.lead_id ? data : l)));
      setEdit((prev) => ({
        ...prev,
        customer_name: data.customer_name || prev.customer_name,
        phone: data.phone || prev.phone,
        email: data.email || prev.email,
        vehicle: data.vehicle || prev.vehicle,
      }));
      if (!smsBody && data.suggested_message) setSmsBody(data.suggested_message);
      if (data.phone) loadConversation(data.phone);
      toast.success(data.phone ? `Got ${data.customer_name || 'customer'} · ${data.phone}` : 'Fetched from portal');
    } catch (e) {
      toast.error(`Couldn't fetch contact — ${e.message}`);
    } finally {
      setEnriching(false);
    }
  };

  // Live-preview the pasted email so the SM can trust the extraction
  useEffect(() => {
    if (!addOpen || !pasteText.trim()) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/comms/leads/parse-preview`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include', body: JSON.stringify({ raw: pasteText }),
        });
        if (r.ok) setPreview(await r.json());
      } catch { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
  }, [pasteText, addOpen]);

  const importLead = async () => {
    if (!pasteText.trim()) return;
    setImporting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/leads/import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ raw: pasteText }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const lead = await r.json();
      toast.success(`Lead imported — ${lead.customer_name}`);
      setAddOpen(false);
      setPasteText('');
      setPreview(null);
      await loadLeads('');
      selectLead(lead);
    } catch (e) {
      toast.error(`Import failed — ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  const saveEdit = async () => {
    if (!selected) return;
    setSavingEdit(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/leads/${selected.lead_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(edit),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const updated = await r.json();
      toast.success('Lead saved');
      setSelected(updated);
      setLeads((prev) => prev.map((l) => (l.lead_id === updated.lead_id ? updated : l)));
      if (updated.phone) loadConversation(updated.phone);
    } catch (e) {
      toast.error(`Save failed — ${e.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Remove lead "${lead.customer_name}"?`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/leads/${lead.lead_id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Lead removed');
      if (selected?.lead_id === lead.lead_id) setSelected(null);
      loadLeads(search);
    } catch (e) {
      toast.error(`Delete failed — ${e.message}`);
    }
  };

  const sendSms = async () => {
    if (!selected?.phone || !smsBody.trim()) return;
    setSendingSms(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/comms/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ mobile: selected.phone, body: smsBody.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      toast[data.status === 'dry_run' ? 'warning' : 'success'](data.status === 'dry_run' ? 'SMS logged (dry-run)' : 'SMS sent');
      setSmsBody('');
      if (selected.status === 'new') {
        setEdit((e) => ({ ...e, status: 'contacted' }));
      }
      loadConversation(selected.phone);
    } catch (e) {
      toast.error(`Send failed — ${e.message}`);
    } finally {
      setSendingSms(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="autohero-leads">
      {/* LEFT — lead list */}
      <div className={`${selected ? 'hidden lg:block' : ''} lg:col-span-1`}>
        <Card className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <Input
              placeholder="Search new leads…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              data-testid="autohero-search"
            />
            <Button size="sm" className="h-8 flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" onClick={pollNow} disabled={polling} data-testid="autohero-poll-now" title="Log into the portal and pull any new leads right now (also runs automatically every hour)">
              {polling ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Poll now
            </Button>
            <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700 flex-shrink-0" onClick={() => setAddOpen(true)} data-testid="autohero-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
          {pollStatus && (() => {
            const ei = pollStatus.email_ingest || {};
            const lp = pollStatus.last_poll;
            const fmt = (s) => new Date(s).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
            return (
              <div className="mb-2 space-y-0.5" data-testid="autohero-poll-status">
                {ei.configured ? (
                  <p className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${ei.last?.error ? 'bg-red-500' : ei.enabled ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                    {ei.enabled ? 'Email auto-import ON — checks every 3 min' : 'Email auto-import off in preview — use Poll now'}
                    {ei.last?.error ? (
                      <span className="text-red-600 font-medium">· Last attempt FAILED — {ei.last.error}</span>
                    ) : ei.last?.at ? (
                      <span>· Last checked {fmt(ei.last.at)} ({ei.last.imported ?? 0} new)</span>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-600 flex items-center gap-1.5 flex-wrap">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    Email auto-import not connected yet — leads only arrive via manual Add
                  </p>
                )}
                <p className="text-[11px] text-slate-400 flex items-center gap-1.5 flex-wrap">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${lp?.error ? 'bg-red-400' : 'bg-slate-300'}`} />
                  {lp?.error
                    ? 'Portal auto-fetch is blocked by Auto Hero (Cloudflare security)'
                    : pollStatus.auto_poll_enabled
                      ? 'Portal auto-fetch ON — checks hourly'
                      : 'Portal auto-fetch off in preview'}
                  {lp?.at && !lp?.error && <span>· Last checked {fmt(lp.at)} ({lp.imported ?? 0} new of {lp.checked ?? 0})</span>}
                </p>
              </div>
            );
          })()}

          <div className="space-y-1 max-h-[calc(100vh-320px)] overflow-y-auto pr-1" data-testid="autohero-list">
            {loading && leads.length === 0 && (
              <div className="py-6 text-center text-slate-400 text-sm"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
            )}
            {!loading && leads.length === 0 && (
              <div className="py-8 text-center text-slate-400 text-sm">
                <Wrench className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No new leads yet.<br />Paste a lead email with “Add”.
              </div>
            )}
            {leads.map((l) => (
              <div
                key={l.lead_id}
                onClick={() => selectLead(l)}
                className={`px-2.5 py-2 rounded-md cursor-pointer transition ${selected?.lead_id === l.lead_id ? 'bg-indigo-50 ring-1 ring-indigo-300' : 'hover:bg-slate-50'}`}
                data-testid={`autohero-row-${l.lead_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900 truncate">{l.customer_name}</p>
                  <Badge className={`text-[9px] px-1.5 capitalize ${STATUS_STYLE[l.status] || ''}`}>{l.status}</Badge>
                </div>
                <p className="text-[11px] text-indigo-700 truncate">{l.intent || l.services || 'New lead'}</p>
                <p className="text-[10px] text-slate-400">
                  {l.phone ? l.phone : 'phone in portal'} · Requested {fmtWhen(l.received_at || l.created_at)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* RIGHT — lead detail */}
      <div className={`${selected ? '' : 'hidden lg:block'} lg:col-span-2`}>
        <Card className="p-4 h-[calc(100vh-260px)] overflow-y-auto">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              Select a lead — or paste a new lead email with “Add”.
            </div>
          ) : (
            <div className="space-y-3" data-testid="autohero-detail">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 truncate">{selected.customer_name}</h3>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    {selected.intent && <Badge variant="outline" className="text-[10px]">{selected.intent}</Badge>}
                    {selected.services && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">{selected.services}</Badge>}
                    <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700 bg-indigo-50">New Lead</Badge>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => deleteLead(selected)} data-testid="autohero-delete">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* iter200 — Primary action: build a quote for this lead. */}
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={() => buildQuote(selected)}
                disabled={building}
                data-testid="autohero-build-quote"
              >
                {building
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting quote…</>
                  : <><FileText className="h-4 w-4 mr-2" /> Build Quote</>}
              </Button>

              {/* Customer message */}
              {selected.message && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5" data-testid="autohero-message">
                  <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-1">Customer message</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}

              {/* iter194 — auto-fetch phone/email from the portal (logs in
                  on the backend), with the manual portal link as a fallback. */}
              {(selected.quote_url || selected.portal_url) && (
                <div className="space-y-1.5" data-testid="autohero-enrich-block">
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={fetchContact}
                    disabled={enriching}
                    data-testid="autohero-fetch-contact"
                  >
                    {enriching
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching contact…</>
                      : <><Phone className="h-4 w-4 mr-2" /> {selected.phone ? 'Refresh customer contact' : 'Fetch customer name & number'}</>}
                  </Button>
                  {selected.enriched_at && selected.phone && (
                    <p className="text-[10px] text-emerald-600 flex items-center gap-1" data-testid="autohero-enrich-ok">
                      <Check className="h-3 w-3" /> Customer contact auto-fetched.
                    </p>
                  )}
                  {selected.enrich_error && !selected.phone && (
                    <p className="text-[10px] text-amber-600" data-testid="autohero-enrich-error">
                      Couldn't auto-fetch ({selected.enrich_error}). Try again or open the portal manually.
                    </p>
                  )}
                  {selected.portal_url && (
                    <button
                      type="button"
                      className="w-full text-[11px] text-slate-500 hover:text-orange-600 inline-flex items-center justify-center gap-1"
                      onClick={() => window.open(selected.portal_url, '_blank', 'noopener,noreferrer')}
                      data-testid="autohero-open-portal"
                    >
                      <ExternalLink className="h-3 w-3" /> Or open the lead in the portal manually
                    </button>
                  )}
                </div>
              )}

              {/* Editable contact + status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="text-xs">
                  <span className="flex items-center gap-1 text-slate-500 mb-0.5"><Phone className="h-3 w-3" /> Phone</span>
                  <Input value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} placeholder="04.. from portal" className="h-8 text-sm" data-testid="autohero-phone" />
                </label>
                <label className="text-xs">
                  <span className="flex items-center gap-1 text-slate-500 mb-0.5"><Mail className="h-3 w-3" /> Email</span>
                  <Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="from portal" className="h-8 text-sm" data-testid="autohero-email" />
                </label>
                <label className="text-xs">
                  <span className="flex items-center gap-1 text-slate-500 mb-0.5"><Car className="h-3 w-3" /> Vehicle</span>
                  <Input value={edit.vehicle} onChange={(e) => setEdit({ ...edit, vehicle: e.target.value })} placeholder="e.g. Honda CR-V" className="h-8 text-sm" data-testid="autohero-vehicle" />
                </label>
                <label className="text-xs">
                  <span className="text-slate-500 mb-0.5 block">Status</span>
                  <select
                    value={edit.status}
                    onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                    className="h-8 w-full text-sm border border-slate-200 rounded-md px-2 capitalize"
                    data-testid="autohero-status"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <label className="text-xs block">
                <span className="text-slate-500 mb-0.5 block">Notes</span>
                <Textarea value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={2} className="text-sm resize-none" data-testid="autohero-notes" />
              </label>
              <Button size="sm" onClick={saveEdit} disabled={savingEdit} className="bg-slate-800 hover:bg-slate-900" data-testid="autohero-save">
                {savingEdit ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />} Save lead
              </Button>

              {/* SMS the lead (once phone is filled) */}
              <div className="border-t border-slate-200 pt-3">
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-1 flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" /> Message the customer
                </p>
                {edit.phone ? (
                  <>
                    {conversation.length > 0 && (
                      <div className="max-h-40 overflow-y-auto space-y-1.5 mb-2 p-2 bg-slate-50 rounded" data-testid="autohero-conversation">
                        {conversation.map((m, i) => (
                          <div key={i} className={`flex ${m.direction === 'in' ? 'justify-start' : 'justify-end'}`}>
                            <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs ${m.direction === 'in' ? 'bg-white border border-slate-200 text-slate-800' : 'bg-indigo-600 text-white'}`}>
                              <p className="whitespace-pre-wrap break-words">{m.body}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Type an SMS to this lead…" rows={2} className="text-sm resize-none flex-1" data-testid="autohero-sms-body" />
                      <Button onClick={sendSms} disabled={sendingSms || !smsBody.trim()} className="bg-indigo-600 hover:bg-indigo-700 self-end" data-testid="autohero-sms-send">
                        {sendingSms ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Save the phone first if you just pasted it. Replies appear here + in your Message inbox.</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-400">Open the portal, copy the customer's phone into the field above and Save — then you can SMS them here.</p>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Add-lead modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAddOpen(false)}>
          <Card className="w-full max-w-xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-900 flex items-center gap-1.5"><ClipboardPaste className="h-4 w-4 text-indigo-600" /> Add new lead</h3>
              <button onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-2">Paste the full lead email (including the link). We'll pull out the name, service and message automatically.</p>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="Paste the lead email here…"
              rows={7}
              className="text-sm mb-2"
              data-testid="autohero-paste"
            />
            {preview && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 mb-2 text-xs space-y-0.5" data-testid="autohero-preview">
                <p><span className="text-slate-500">Name:</span> <b>{preview.customer_name || '—'}</b></p>
                <p><span className="text-slate-500">Looking for:</span> {preview.intent || preview.services || '—'}</p>
                <p className="truncate"><span className="text-slate-500">Message:</span> {preview.message || '—'}</p>
                <p><span className="text-slate-500">Portal link:</span> {preview.portal_url ? '✓ detected' : '✗ not found'}</p>
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={importLead} disabled={importing || !pasteText.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="autohero-import-confirm">
                {importing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />} Import lead
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
